import Button from "components/_button/form/submit"
import { selectStyles } from "constants/form"
import { useGetServiceType } from "hooks/useRequest/form"
import { useGetCities, useGetProvinces } from "hooks/useRequest/location/location"
import { useCreateProject } from "hooks/useRequest/project"
import { useCreateUser } from "hooks/useRequest/user"
import { useRouter } from "next/router"
import { Dispatch, FC, FormEvent, SetStateAction, useCallback, useEffect, useState } from "react"
import ReactGA from 'react-ga'
import Select from 'react-select'
import { toast } from "react-toastify"
import { ICity } from "rules/interface/city/city.interface"
import IProjectCreate from "rules/interface/project/create.interface"
import { IProvince } from "rules/interface/province/province.interface"
import lastProjectService from "services/last-project"
import tokenService from "services/token.service"
import visitedPageService from "services/visited-page.service"
import styles from "./index.module.scss"



interface IError {
    province?: string
    city?: string
}


interface IProps {
    next: () => void
    values: IProjectCreate
    setValues: Dispatch<SetStateAction<IProjectCreate>>
}


const LocationStep: FC<IProps> = ({ next, values, setValues }) => {

    const { data: provinceData, isLoading: provinceIsLoading, isSuccess: provinceIsSuccess } = useGetProvinces()

    const { data: cities, isLoading: citiesIsLoading, isSuccess: citiesIsSuccess } = useGetCities({
        enabled: Boolean(values?.province),
        params: {
            province: values?.province?._id
        }
    })

    const { mutateAsync: createProjectMutate, isError: createProjectIsError, error: createProjectError, isLoading: createProjectIsLoading }: any = useCreateProject()
    const { mutateAsync: createUserMutate, isLoading: createUserIsLoading }: any = useCreateUser()

    const { data: serviceTypeData, isLoading: serviceTypeIsLoading } = useGetServiceType()

    const router = useRouter()



    const [errors, setErrors] = useState<IError>({});


    // convert service name to id
    const convertServiceToId = (service: string) => {
        return serviceTypeData?.data?.find(res => res.name === service)?._id
    }

    const onSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (validationError()) {
            ReactGA.event({
                category: 'Submit',
                action: 'Click',
                label: 'form-step1'
            });

            createUserMutate({
                phoneNumber: values.user.phoneNumber,
                province: values.province?._id,
                city: values.city?._id,
                forceVerify: false,
            }).then(res => {
                createProjectMutate({
                    user: res.data?._id,
                    serviceType: "cabinet",
                    serviceTypeId: convertServiceToId("cabinet"),
                    location: {
                        province: values?.province?._id,
                        city: values.city?._id,
                    },
                    mightBeInterestedInInstallments: true,
                    submitInfo: {
                        ...(router.query?.utm_source && {
                            sourceOfEntry: (router.query?.utm_source as string),
                        }),
                        formType: router.query?.utm_campaign ?? router.query?.form_type ?? undefined,
                        sourcePage: window.location.href,
                        previousPage: localStorage.getItem("prevPath"),
                        landingPage: visitedPageService.getPages()?.[0]
                    },
                    visitedPages: visitedPageService.getPages(),
                }).then(projectRes => {
                    lastProjectService.setProject({
                        id: projectRes.data._id,
                        createdDate: projectRes.data.createdAt,
                        project: projectRes.data
                    })

                    tokenService.setUser({
                        ...res.data,
                        id: res.data?._id,
                        projectId: projectRes?.data._id,
                    })

                    next()
                })
            }).catch(err => {
                toast.error(err?.response?.data?.message)
            })
        }
    }, [values])


    useEffect(() => {
        if (createProjectIsError) {
            toast.error(createProjectError?.response?.data?.message)
        }
    }, [createProjectIsError])


    // validate input's
    // have two state for handle validation
    // one of them for all fields can validate
    // another one for save error message of each input error
    const validationError = () => {
        let error: IError = {};
        // by default form validate is true
        // if we have one error or more
        // return false and waiting for fill data by client
        let formIsValid = true;

        // province
        if (!values?.province) {
            formIsValid = false;
            error["province"] = "استان خود را انتخاب کنید";
        }

        // validate city of province
        if (!(cities?.data?.filter(res => res.stat === values.province?._id)?.find(res => res._id === values.city?._id))) {
            formIsValid = false;
            error["city"] = "شهر انتخابی جزو استان انتخاب شده نمیباشد";
            setValues(prev => ({ ...prev, city: null }))
        }

        // city
        if (!values?.city) {
            formIsValid = false;
            error["city"] = "شهر خود را انتخاب کنید";
        }

        setErrors(error);
        return formIsValid;
    }



    return (
        <article className="flex flex-column gap-15">
            <form onSubmit={onSubmit} className="flex flex-column gap-25">
                <section className="flex flex-column gap-15">
                    <div className={styles.form_item + " _validate relative input_block w100-mb flex flex-column jcc"}>
                        <label className={styles.wrapper__form_item}>
                            <span className={styles.label__wrapper}>استان</span>
                            <Select
                                className={styles.select__wrapper + ` relative p-0 w100-mb ${errors?.province ? "input_error" : ""}`}
                                placeholder="استان را انتخاب کنید."
                                getOptionLabel={(option: IProvince) => option.name}
                                getOptionValue={(option: IProvince) => option._id}
                                options={provinceIsSuccess && provinceData?.data}
                                isLoading={provinceIsLoading}
                                openMenuOnClick
                                value={values?.province}
                                isRtl
                                isSearchable
                                onChange={(val: IProvince) => setValues(prev => ({ ...prev, province: val, city: null }))}
                                styles={{
                                    ...selectStyles,
                                    control: (base) => ({
                                        ...base,
                                        borderRadius: "8px",
                                        backgroundColor: "#FFF",
                                        paddingBlock: "4px",
                                        border: "none"
                                    }),
                                    placeholder: (base) => ({
                                        ...base,
                                        fontSize: "14px",
                                        fontWeight: 300,
                                        color: "#D9D9D9"
                                    })
                                }}
                                components={{
                                    IndicatorSeparator: null,
                                }}
                            />
                            {errors?.province && <span className="input_alert">{errors?.province}</span>}
                        </label>
                    </div>

                    <div className={styles.form_item + " _validate input_block w100-mb flex flex-column jcc"}>
                        <label className={styles.wrapper__form_item}>
                            <span className={styles.label__wrapper}>شهر</span>
                            <Select
                                className={styles.select__wrapper + ` p-0 w100-mb ${errors?.city ? "input_error" : ""}`}
                                placeholder={!values.province ? "ابتدا استان را انتخاب کنید" : "شهر را انتخاب کنید."}
                                getOptionLabel={(option: ICity) => option.name}
                                getOptionValue={(option: ICity) => option._id}
                                options={citiesIsSuccess && cities?.data}
                                value={values?.city}
                                isLoading={citiesIsLoading}
                                isRtl
                                isSearchable
                                captureMenuScroll
                                isDisabled={!values.province || citiesIsLoading}
                                onChange={(val: ICity) => setValues(prev => ({ ...prev, city: val }))}
                                styles={{
                                    ...selectStyles,
                                    control: (base) => ({
                                        ...base,
                                        borderRadius: "8px",
                                        paddingBlock: "4px",
                                        backgroundColor: "#FFF",
                                        border: "none"
                                    }),
                                    placeholder: (base) => ({
                                        ...base,
                                        fontSize: "14px",
                                        fontWeight: 300,
                                        color: "#D9D9D9"
                                    })
                                }}
                                components={{
                                    IndicatorSeparator: null,
                                }}
                            />
                            {errors?.city && <span className="input_alert">{errors?.city}</span>}
                        </label>
                    </div>
                </section>
                <Button type="submit" className="flex align-i-center jcc gap-10" id="form-choose-city-province">
                    مرحله بعدی
                    <svg width="8" height="16" viewBox="0 0 9 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.00009 16.9201L1.48009 10.4001C0.710088 9.63008 0.710088 8.37008 1.48009 7.60008L8.00009 1.08008" stroke="#EEEEEE" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </Button>
            </form>
        </article>
    )
}

export default LocationStep